import styles from './VMainArtesianBanner.module.css'
import { useEffect, useRef, useState } from 'react'

// Конечная глубина скважины (м) — главный акцент блока.
const TARGET_DEPTH = 500;

// Поднимающиеся пузырьки (артезианская вода идёт вверх под давлением).
// Заданы статично, чтобы позиции не "прыгали" при ре-рендере.
const BUBBLES = [
  { left: '6%',  size: 12, duration: 8,   delay: 0   },
  { left: '14%', size: 7,  duration: 6,   delay: 1.2 },
  { left: '22%', size: 16, duration: 9,   delay: 2.4 },
  { left: '32%', size: 9,  duration: 7,   delay: 0.6 },
  { left: '41%', size: 6,  duration: 5.5, delay: 3   },
  { left: '50%', size: 14, duration: 8.5, delay: 1.8 },
  { left: '59%', size: 8,  duration: 6.5, delay: 0.3 },
  { left: '68%', size: 11, duration: 7.5, delay: 2.1 },
  { left: '77%', size: 6,  duration: 5,   delay: 1   },
  { left: '85%', size: 15, duration: 9,   delay: 3.4 },
  { left: '92%', size: 9,  duration: 6.8, delay: 0.9 },
];

const VMainArtesianBanner = () => {
  const sectionRef = useRef(null);
  const [depth, setDepth] = useState(0);
  const [started, setStarted] = useState(false);

  // Запуск анимации, когда блок попадает в зону видимости.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setStarted(true);
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Счётчик глубины 0 → 500 с плавным замедлением.
  useEffect(() => {
    if (!started) return;

    const duration = 1700;
    let raf;
    let startTs;

    const tick = (ts) => {
      if (startTs === undefined) startTs = ts;
      const progress = Math.min((ts - startTs) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setDepth(Math.round(eased * TARGET_DEPTH));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started]);

  return (
    <section ref={sectionRef} className={styles.banner__artesian}>
      {/* Поднимающиеся пузырьки */}
      <div className={styles.bubbles} aria-hidden="true">
        {BUBBLES.map((b, i) => (
          <span
            key={i}
            className={styles.bubble}
            style={{
              left: b.left,
              width: b.size,
              height: b.size,
              animationDuration: `${b.duration}s`,
              animationDelay: `${b.delay}s`,
            }}
          />
        ))}
      </div>

      <div className={styles.artesian__container}>
        <div className={styles.artesian__icon}>
          <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="waterFill" x1="60" y1="20" x2="60" y2="104">
                <stop offset="0%" stopColor="rgba(255,255,255,0.5)"/>
                <stop offset="100%" stopColor="rgba(180,225,255,0.22)"/>
              </linearGradient>
              <clipPath id="circleClip">
                <circle cx="60" cy="60" r="44"/>
              </clipPath>
            </defs>

            {/* Спокойные расходящиеся круги */}
            <circle className={styles.ripple1} cx="60" cy="60" r="44" stroke="rgba(255,255,255,0.35)" strokeWidth="2" fill="none"/>
            <circle className={styles.ripple2} cx="60" cy="60" r="44" stroke="rgba(255,255,255,0.25)" strokeWidth="2" fill="none"/>

            {/* Подложка эмблемы */}
            <circle cx="60" cy="60" r="44" fill="rgba(255,255,255,0.07)"/>

            {/* Вода с мягко колышущейся поверхностью (обрезана по кругу) */}
            <g clipPath="url(#circleClip)">
              <g className={styles.water}>
                <path className={styles.waveA} d="M-40,58 Q-30,52 -20,58 T0,58 T20,58 T40,58 T60,58 T80,58 T100,58 T120,58 T140,58 T160,58 L160,112 L-40,112 Z" fill="url(#waterFill)"/>
                <path className={styles.waveB} d="M-40,63 Q-30,57 -20,63 T0,63 T20,63 T40,63 T60,63 T80,63 T100,63 T120,63 T140,63 T160,63 L160,112 L-40,112 Z" fill="rgba(255,255,255,0.16)"/>
              </g>

              {/* Пузырьки, поднимающиеся к поверхности */}
              <circle className={styles.tubeBubble1} cx="50" cy="98" r="2.5" fill="rgba(255,255,255,0.7)"/>
              <circle className={styles.tubeBubble2} cx="66" cy="98" r="1.8" fill="rgba(255,255,255,0.6)"/>
              <circle className={styles.tubeBubble3} cx="58" cy="98" r="3"   fill="rgba(255,255,255,0.7)"/>
            </g>

            {/* Контур эмблемы */}
            <circle cx="60" cy="60" r="44" stroke="rgba(255,255,255,0.55)" strokeWidth="2.5" fill="none"/>
          </svg>
        </div>

        <div className={styles.artesian__content}>
          <p className={styles.artesian__label}>Наше главное преимущество</p>
          <p className={styles.artesian__text}>
            Вода натурального происхождения из артезианской скважины
          </p>

          <div className={styles.artesian__stat}>
            <span className={styles.statValue}>{depth}</span>
            <span className={styles.statUnit}>м</span>
            <span className={styles.statCaption}>глубина скважины</span>
          </div>
        </div>
      </div>
    </section>
  )
}

export default VMainArtesianBanner
